import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import type { AssayFloor, AssayPlateCell } from '../api'
import {
  CREDIBILITY, CREDIBILITY_MEANING, RELIABILITY, RELIABILITY_MEANING, meetsFloor,
} from './scale'

/**
 * The assay plate — thirty-six Admiralty cells and the rule that runs through them.
 *
 * ## Why this is DOM and not canvas
 *
 * The chronology beside it is a canvas because it draws thousands of marks. This draws
 * thirty-six, and every one of them is a control. Rendering it as buttons means it is
 * keyboard-reachable, screen-reader-readable and selectable with the same gestures as the
 * rest of the app — properties a canvas would have to reimplement badly. The plate is also
 * the thing somebody screenshots into a brief, and hairline borders at device resolution
 * print better than anything a 2D context will give you.
 *
 * ## Area carries the count, colour carries the side of the rule
 *
 * Each cell holds a square whose AREA is proportional to its item count — so four times the
 * items is four times the ink, not sixteen. Colour says one thing only: whether the cell is
 * inside the floor. Using a colour ramp for the count as well would put two quantities on one
 * channel and make a busy unusable cell shout louder than a quiet decisive one.
 *
 * ## The floor is a staircase, never a diagonal
 *
 * `C3` means reliability at or better than C AND credibility at or better than 3. That region
 * is a rectangle, so the rule around it turns right angles. Drawing it as a diagonal — which
 * is what a reader expects from two ordered axes — would suggest a trade-off between the two
 * that the standard does not permit: no amount of source reliability rescues a claim nobody
 * can judge.
 */

export interface GradeKey { reliability: string, credibility: string }

export const keyOf = (g: GradeKey) => `${g.reliability}${g.credibility}`

export function Plate({ cells, floor, selected, onToggle, onClear }: {
  cells: AssayPlateCell[]
  floor: AssayFloor
  /** Grade keys the reader has picked, e.g. `["C3", "D5"]`. Empty means no grade filter. */
  selected: Set<string>
  onToggle: (keys: string[]) => void
  onClear: () => void
}) {
  const byKey = useMemo(() => {
    const map = new Map<string, AssayPlateCell>()
    for (const cell of cells) map.set(keyOf(cell), cell)
    return map
  }, [cells])

  const peak = useMemo(() => Math.max(1, ...cells.map((c) => c.items)), [cells])
  const total = useMemo(() => cells.reduce((n, c) => n + c.items, 0), [cells])

  const rowTotals = useMemo(() => RELIABILITY.map((r) =>
    cells.filter((c) => c.reliability === r).reduce((n, c) => n + c.items, 0)), [cells])
  const colTotals = useMemo(() => CREDIBILITY.map((c) =>
    cells.filter((cell) => cell.credibility === c).reduce((n, cell) => n + cell.items, 0)),
    [cells])
  const rowPeak = Math.max(1, ...rowTotals)
  const colPeak = Math.max(1, ...colTotals)

  return (
    <figure className="m-0 flex min-w-0 flex-col gap-2">
      {/* One line, and it must stay one line: the caption sits directly above a grid whose
          columns are 26px wide, and a caption that wraps pushes the whole plate down by a row
          every time the reader picks a cell. */}
      <figcaption className="flex items-baseline justify-between gap-3 whitespace-nowrap">
        <span className="axis-plate-eyebrow">Every item, by source and by claim</span>
        {selected.size > 0 ? (
          <button type="button" onClick={onClear}
                  className="axis-plate-mini underline underline-offset-2">
            Clear {selected.size} grade{selected.size === 1 ? '' : 's'}
          </button>
        ) : (
          <span className="axis-plate-mini">Reliability × credibility</span>
        )}
      </figcaption>

      <div className="grid gap-x-1.5 gap-y-1"
           style={{ gridTemplateColumns: '18px repeat(6, minmax(26px, 1fr)) 40px' }}>
        {/* ── the six rows ── */}
        {RELIABILITY.map((rel, rowIndex) => (
          <Row key={rel}
               rel={rel}
               rowTotal={rowTotals[rowIndex] ?? 0}
               rowPeak={rowPeak}
               floor={floor}
               peak={peak}
               total={total}
               byKey={byKey}
               selected={selected}
               onToggle={onToggle} />
        ))}

        {/* ── the credibility axis ── */}
        <span />
        {CREDIBILITY.map((cred) => (
          <button
            key={cred}
            type="button"
            onClick={() => onToggle(RELIABILITY.map((r) => `${r}${cred}`))}
            title={`Credibility ${cred} — ${CREDIBILITY_MEANING[cred]}. Click to select the column.`}
            className="axis-plate-axis"
          >
            {cred}
          </button>
        ))}
        <span />

        {/* ── the marginal over credibility ── */}
        <span />
        {CREDIBILITY.map((cred, i) => (
          <span key={cred} className="flex h-6 items-end justify-center"
                title={`${colTotals[i]} item${colTotals[i] === 1 ? '' : 's'} graded ${cred}`}>
            <i className="block w-full"
               style={{
                 height: `${Math.max(1, ((colTotals[i] ?? 0) / colPeak) * 22)}px`,
                 background: 'var(--plate-ink-faint)',
                 opacity: 0.55,
               }} />
          </span>
        ))}
        <span />
      </div>

      <p className="axis-plate-mini leading-relaxed">
        <b className="font-normal" style={{ color: 'var(--plate-ink-dim)' }}>
          {floor.reliability}{floor.credibility}
        </b>{' '}
        is the brief floor: the heavy rule encloses everything this room will quote. Area is the
        count. <span style={{ color: 'var(--plate-dispute)' }}>▲</span> marks a cell holding a
        disputed or debunked item.
      </p>
    </figure>
  )
}

function Row({ rel, rowTotal, rowPeak, floor, peak, total, byKey, selected, onToggle }: {
  rel: string
  rowTotal: number
  rowPeak: number
  floor: AssayFloor
  peak: number
  total: number
  byKey: Map<string, AssayPlateCell>
  selected: Set<string>
  onToggle: (keys: string[]) => void
}) {
  return (
    <>
      <button
        type="button"
        onClick={() => onToggle(CREDIBILITY.map((c) => `${rel}${c}`))}
        title={`Reliability ${rel} — ${RELIABILITY_MEANING[rel]}. Click to select the row.`}
        className="axis-plate-axis"
      >
        {rel}
      </button>

      {CREDIBILITY.map((cred) => {
        const key = `${rel}${cred}`
        const cell = byKey.get(key)
        const items = cell?.items ?? 0
        const inside = meetsFloor(rel, cred, floor)
        // The rule is drawn on the cells that form the region's edge, so it is a real border
        // between two cells rather than an overlay that can drift out of register with them.
        const edgeRight = inside && cred === floor.credibility
        const edgeBottom = inside && rel === floor.reliability
        // sqrt, so AREA is the quantity. A linear side would make four items look like
        // sixteen, which is the oldest way a square symbol lies.
        const side = items === 0 ? 0 : 4 + Math.sqrt(items / peak) * 20

        return (
          <button
            key={key}
            type="button"
            aria-pressed={selected.has(key)}
            onClick={() => onToggle([key])}
            title={`${key} — ${RELIABILITY_MEANING[rel]}, ${CREDIBILITY_MEANING[cred]}\n`
              + `${items} item${items === 1 ? '' : 's'}`
              + (total > 0 ? ` · ${((items / total) * 100).toFixed(1)}% of the window` : '')
              + (cell?.contested ? `\n${cell.contested} disputed or debunked` : '')
              + `\n${inside ? 'Inside the floor — quotable' : 'Below the floor — not quotable'}`}
            className={cn('axis-plate-cell', selected.has(key) && 'axis-plate-cell-on')}
            style={{
              borderRightWidth: edgeRight ? 2 : 1,
              borderBottomWidth: edgeBottom ? 2 : 1,
              borderRightColor: edgeRight ? 'var(--plate-rule-strong)' : undefined,
              borderBottomColor: edgeBottom ? 'var(--plate-rule-strong)' : undefined,
            }}
          >
            {side > 0 ? (
              <i className="block"
                 style={{
                   width: side, height: side,
                   background: inside ? 'var(--plate-above)' : 'var(--plate-below)',
                   opacity: inside ? 0.88 : 0.7,
                 }} />
            ) : (
              <i className="axis-plate-empty" />
            )}
            {cell && cell.contested > 0 ? (
              <i className="absolute top-[2px] right-[2px] size-0 border-x-[3px] border-b-[5px]
                            border-x-transparent"
                 style={{ borderBottomColor: 'var(--plate-dispute)' }} />
            ) : null}
            {items > 0 ? <em className="axis-plate-count">{items}</em> : null}
          </button>
        )
      })}

      <span className="flex items-center gap-1" title={`${rowTotal} item${rowTotal === 1 ? '' : 's'} from ${RELIABILITY_MEANING[rel]} sources`}>
        <i className="block h-[7px]"
           style={{
             width: `${Math.max(1, (rowTotal / rowPeak) * 26)}px`,
             background: 'var(--plate-ink-faint)',
             opacity: 0.55,
           }} />
        <em className="axis-plate-mini not-italic tabular-nums">{rowTotal}</em>
      </span>
    </>
  )
}
